import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fmtDate } from "@/lib/api";
import { useApi, post, Panel, PageHead, Stat, DataTable, StatusBadge, Tabs, inr } from "@/crm/ui";

const TABS = ["All", "Pending", "Partial", "Disbursed", "Cancelled", "Reversed"];

export default function Disbursement() {
  const nav = useNavigate();
  const [tab, setTab] = useState("All");
  const [row, setRow] = useState(null);
  const [form, setForm] = useState({});
  const list = useApi(`/crm/disbursements?status=${tab === "All" ? "all" : tab}`, [tab]);
  const d = list.data || {};
  const s = d.summary || {};

  const submit = async () => {
    const r = await post(`/crm/applications/${row.application_id}/disbursement`, form,
      "Disbursement recorded");
    if (r) {
      setRow(null); setForm({}); list.reload();
      if (r.payout) nav("/crm/payouts");
    }
  };

  return (
    <div className="space-y-6" data-testid="disbursement-page">
      <PageHead eyebrow="CRM" title="Disbursement desk"
                subtitle="Record full or partial disbursement tranches against sanctioned applications. Totals can never exceed the sanctioned amount and each tranche generates the payout server-side." />
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat label="Cases" value={s.cases ?? "—"} testid="dis-cases" />
        <Stat label="Sanctioned" value={inr(s.sanctioned)} testid="dis-sanctioned" />
        <Stat label="Disbursed" value={inr(s.disbursed)} testid="dis-disbursed" />
        <Stat label="Pending" value={inr(s.pending)} testid="dis-pending" />
      </div>
      <Tabs tabs={TABS} active={tab} onChange={setTab} testid="disbursement-tabs" />
      <Panel title={`Disbursement pipeline (${(d.rows || []).length})`} testid="disbursements-panel">
        <DataTable testid="disbursements-table" rows={d.rows}
                   empty="No sanctioned or disbursed applications in this view."
                   columns={[{ key: "application_id", label: "Application" },
                     { key: "customer_name", label: "Customer" }, { key: "bank_name", label: "Bank" },
                     { key: "location_name", label: "Location" }, { key: "product_name", label: "Product" },
                     { key: "employee_id", label: "Employee" }, { key: "partner_id", label: "Partner" },
                     { key: "sanction_amount", label: "Sanctioned", right: true, render: (r) => inr(r.sanction_amount) },
                     { key: "disbursed_amount", label: "Disbursed", right: true, render: (r) => inr(r.disbursed_amount) },
                     { key: "pending_amount", label: "Pending", right: true, render: (r) => inr(r.pending_amount) },
                     { key: "tranches", label: "Tranches", right: true, render: (r) => (r.tranches || []).length },
                     { key: "disbursement_date", label: "Last Disb.", render: (r) => fmtDate(r.disbursement_date) },
                     { key: "disbursement_status", label: "Disb. Status", render: (r) => <StatusBadge value={r.disbursement_status} /> },
                     { key: "status", label: "Application", render: (r) => <StatusBadge value={r.status} /> },
                     { key: "actions", label: "Actions", render: (r) => (
                       <span className="flex gap-1">
                         <Button size="sm" variant="outline" className="bg-white text-[10px]"
                                 data-testid={`record-disbursement-${r.application_id}`}
                                 onClick={() => { setRow(r); setForm({ disbursement_status: "Disbursed", amount: r.pending_amount || r.sanction_amount }); }}>
                           Record</Button>
                         <Button size="sm" variant="outline" className="bg-white text-[10px]"
                                 data-testid={`disbursement-lead-${r.application_id}`}
                                 onClick={() => nav(`/crm/leads/${r.lead_id}`)}>Lead</Button>
                       </span>) }]} />
      </Panel>

      <Dialog open={!!row} onOpenChange={() => setRow(null)}>
        <DialogContent className="max-w-lg bg-white" data-testid="disbursement-dialog">
          <DialogHeader><DialogTitle>Record disbursement — {row?.application_id}</DialogTitle></DialogHeader>
          {row && (
            <div className="grid gap-3 sm:grid-cols-2">
              <p className="sm:col-span-2 text-xs text-muted-foreground">
                Sanctioned {inr(row.sanction_amount)} · already disbursed {inr(row.disbursed_amount)} ·
                pending {inr(row.pending_amount)}
              </p>
              <div className="space-y-1">
                <Label className="text-xs">Disbursement status</Label>
                <select className="h-9 w-full rounded-md border border-input bg-white px-2 text-sm"
                        data-testid="disbursement-status-select" value={form.disbursement_status || ""}
                        onChange={(e) => setForm((f) => ({ ...f, disbursement_status: e.target.value }))}>
                  {["Disbursed", "Partial", "Pending", "Cancelled", "Reversed"].map((x) => (
                    <option key={x} value={x}>{x}</option>))}
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Amount</Label>
                <Input type="number" data-testid="disbursement-amount-input" value={form.amount ?? ""}
                       onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Disbursement date</Label>
                <Input type="date" data-testid="disbursement-date-field" value={form.disbursement_date || ""}
                       onChange={(e) => setForm((f) => ({ ...f, disbursement_date: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Expected payout date</Label>
                <Input type="date" data-testid="expected-payout-date-input" value={form.expected_payout_date || ""}
                       onChange={(e) => setForm((f) => ({ ...f, expected_payout_date: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">UTR / reference</Label>
                <Input data-testid="disbursement-utr-input" value={form.utr || ""}
                       onChange={(e) => setForm((f) => ({ ...f, utr: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Remarks</Label>
                <Input data-testid="disbursement-remarks-input" value={form.remarks || ""}
                       onChange={(e) => setForm((f) => ({ ...f, remarks: e.target.value }))} />
              </div>
            </div>)}
          <DialogFooter>
            <Button variant="outline" className="bg-white" onClick={() => setRow(null)}>Cancel</Button>
            <Button onClick={submit} data-testid="confirm-disbursement-btn">Save disbursement</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
